import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";

import { formatShrinkagePercentage } from "./repacking-utils";

import type { RepackShrinkageItem } from "@artisancode/api-types";

interface Props {
  items: RepackShrinkageItem[];
}

export function RepackShrinkageMobileList({ items }: Props) {
  if (items.length === 0) {
    return (
      <div className="py-12 text-center text-xs text-muted-foreground">
        Belum ada data susut repacking pada periode atau filter ini.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {items.map((item) => {
        const hasShrinkage = item.shrinkageQty > 0;
        const isHigh = item.shrinkagePercentage > 5;

        return (
          <Card key={item.rawMaterialId} className="gap-2 py-3 shadow-xs">
            <CardContent className="space-y-2.5 px-4 text-xs">
              {/* Header: Name + Badge */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-semibold text-sm text-foreground">
                    {item.rawMaterialName}
                  </span>
                  {item.rawMaterialSku && (
                    <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
                      {item.rawMaterialSku}
                    </p>
                  )}
                </div>

                {hasShrinkage ? (
                  <Badge
                    variant="secondary"
                    className={
                      isHigh
                        ? "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 text-[11px] font-semibold"
                        : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-[11px] font-semibold"
                    }
                  >
                    {formatShrinkagePercentage(item.shrinkagePercentage)}
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-[11px]"
                  >
                    0.0%
                  </Badge>
                )}
              </div>

              {/* Quantities Grid */}
              <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-2.5 text-center text-xs">
                <div>
                  <span className="text-[10px] text-muted-foreground block">
                    Target
                  </span>
                  <span className="font-medium text-muted-foreground">
                    {item.expectedQty.toLocaleString("id-ID")}{" "}
                    <span className="text-[10px]">{item.uomName}</span>
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground block">
                    Hasil Aktual
                  </span>
                  <span className="font-medium text-foreground">
                    {item.actualQty.toLocaleString("id-ID")}{" "}
                    <span className="text-[10px]">{item.uomName}</span>
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground block">
                    Total Susut
                  </span>
                  <span
                    className={
                      hasShrinkage
                        ? "font-semibold text-rose-600 dark:text-rose-400"
                        : "text-muted-foreground"
                    }
                  >
                    {item.shrinkageQty.toLocaleString("id-ID")}{" "}
                    <span className="text-[10px]">{item.uomName}</span>
                  </span>
                </div>
              </div>

              {/* Footer: Nilai Susut + Dokumen Count */}
              <div className="flex items-center justify-between pt-1 border-t border-border/50 text-[11px]">
                <span className="text-muted-foreground">
                  {item.documentCount} dokumen repacking
                </span>
                <span className="font-semibold text-rose-600 dark:text-rose-400">
                  {item.shrinkageValue > 0
                    ? `Nilai: ${formatCurrency(item.shrinkageValue)}`
                    : "Tidak ada susut"}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
